import { useMailRuntime } from "../context/MailRuntime";
import { useCallback, useEffect, useRef } from "react";
import { useComposerContext } from "../context/ComposerContext";
import { composerRequest } from '../composerRequest';
import useDelivery from "./useDelivery";

export default function useNewsletterApi({ ensureRoot, getBodyInnerHtml, setEditorHtml }, { saveDraftLocal, clearDraft, capture, restoreDocument }) {
  const ctx = useComposerContext();
  const alive = useRef(true);
  useEffect(()=>{alive.current=true;return()=>{alive.current=false;};},[]);
  const runtime = useMailRuntime();
  const { fetch, getRequiredIdToken } = runtime;
  const { API_GET_NEWSLETTER, API_SAVE_NEWSLETTER, API_LIST_NEWSLETTERS } = runtime.api;
  const delivery = useDelivery({ capture, saveDraftLocal });

  const loadNewsletterFromApi = useCallback(async (id) => {
    if (ctx.operationLock.current) return;
    ctx.operationLock.current = true; ctx.setLoading(true); ctx.setReady(false); ctx.setLoadError('');
    try {
      const response = await composerRequest(runtime, ctx.owner, `${API_GET_NEWSLETTER}?id=${encodeURIComponent(id)}&type=drafts`);
      if (!alive.current) return;
      const data = response.newsletter || response;
      if (data.newsletterId !== id || !data.version) throw new Error('Saved version is unavailable. Retry after the backend release.');
      if (runtime.fixedTemplate && data.audience !== runtime.fixedTemplate.id) throw new Error('This draft belongs to another newsletter. Return to Mail Center and open it from its own audience.');
      const base = ctx.draftKey.slice(0, ctx.draftKey.lastIndexOf('-' + (ctx.newsletterId || 'new')));
      let recovery = null, recoveryUnavailable = false;
      try { recovery = localStorage.getItem(base + '-' + id); } catch { recoveryUnavailable = true; }
      let recovered = false;
      if (recovery) {
        try { const local = JSON.parse(recovery); if (local.owner === ctx.owner && local.newsletterId === id && Date.parse(local.updatedAt) > Date.parse(data.updatedAt || 0) && window.confirm('A newer browser recovery copy exists. Restore these edits to this saved draft? Choose Cancel to open the account copy.')) { restoreDocument({ ...local, newsletterId: id, version: data.version }); recovered = true; } } catch { ctx.setNotice('Browser recovery is unreadable; opening the account copy.'); }
      }
      if (!recovered) restoreDocument({ ...data, newsletterId: id });
      ctx.setReady(true); ctx.setNotice(recovered ? 'Browser edits restored. Save or send to keep them in your account.' : recoveryUnavailable ? 'Saved draft loaded from your account. Browser recovery is unavailable; save changes to your account before leaving.' : 'Saved draft loaded.');
    } catch (error) { ctx.setLoadError(error.message || 'Failed to load newsletter.'); }
    finally { ctx.operationLock.current = false; ctx.setLoading(false); }
  }, [ctx, runtime, restoreDocument, API_GET_NEWSLETTER]);

  const saveDraftServer = useCallback(async (options = {}) => {
    if (ctx.operationLock.current || !ctx.ready) return null;
    if (!ctx.subject.trim()) { ctx.setNotice('Add a subject before saving.'); return null; }
    ctx.operationLock.current = true; ctx.setSaving(true);
    try {
      ensureRoot();
      const id = options.asNew === true ? crypto.randomUUID() : ctx.newsletterId || crypto.randomUUID();
      const payload = { ...capture(), newsletterId: id, version: options.asNew === true ? null : ctx.version };
      // Keep this exact ID after an uncertain request so retry cannot create duplicates.
      ctx.setNewsletterId(id); if (options.asNew === true) ctx.setVersion(null);
      let recoveryFailed = false;
      try { saveDraftLocal({ newsletterId: id, version: payload.version }); } catch { recoveryFailed = true; }
      const data = await composerRequest(runtime, ctx.owner, API_SAVE_NEWSLETTER, 'POST', payload);
      if (!alive.current) return null;
      if (data.newsletterId !== id || !data.version) throw new Error('Save result is uncertain. Your editor content is preserved. Reopen the saved draft to confirm.');
      ctx.setVersion(data.version || null);
      try { saveDraftLocal({ newsletterId: id, version: data.version }); } catch { recoveryFailed = true; }
      // Do not replace ongoing edits with the older snapshot sent to the server.
      ctx.setNotice(recoveryFailed ? 'Draft saved to your account. Browser recovery is unavailable on this device.' : 'Draft saved to your account. Further edits are kept in this browser until you Save again.');
      return id;
    } catch (error) { ctx.setNotice(error.message || 'Save failed.'); return null; }
    finally { ctx.operationLock.current = false; ctx.setSaving(false); }
  }, [ctx, runtime, capture, ensureRoot, saveDraftLocal, API_SAVE_NEWSLETTER]);

  const loadDraftsList = useCallback(async () => {
    try {
      const token = await getRequiredIdToken();
      const res = await fetch(`${API_LIST_NEWSLETTERS}?type=drafts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.newsletters || data.items || [];
      ctx.setDrafts(list);
    } catch {
      console.warn("Failed to load drafts list");
    }
  }, [ctx, fetch, getRequiredIdToken, API_LIST_NEWSLETTERS]);

  return {
    loadNewsletterFromApi,
    saveDraftServer,
    ...delivery,
    loadDraftsList,
  };
}
